import { useState } from 'react';
import { useI18n } from './i18n';

function hiddenKey(user) {
  return `mm-onboarding-hidden-${user.id}`;
}

// Whether the member closed the checklist on this browser.
export function readHidden(user) {
  try {
    return localStorage.getItem(hiddenKey(user)) === '1';
  } catch {
    return false;
  }
}

// The first steps for a new member. Each one ticks itself off from their data.
export function onboardingSteps({ accounts, trades, notes, plays }) {
  const days = Object.values(notes || {});
  return [
    {
      id: 'account',
      title: 'Set up your trading account',
      text: 'Add your prop account and pick a preset so Market Matrix knows your rules.',
      action: 'Add account',
      done: accounts.some((a) => !a.attemptOf && a.size),
    },
    {
      id: 'trades',
      title: 'Import your trades',
      text: 'Upload a TopstepX, Tradovate or NinjaTrader export, or any CSV. Takes seconds.',
      action: 'Import trades',
      done: trades.length > 0,
    },
    {
      id: 'morning',
      title: 'Do your first Morning prep',
      text: 'Check in, read your rules and write today’s plan before the open.',
      action: 'Open Morning prep',
      done: days.some((n) => n.plan || (n.moodBefore && n.moodBefore.length) || (n.morningDone && n.morningDone.length)),
    },
    {
      id: 'review',
      title: 'Tag and grade a trade',
      text: 'Click a trade to add its setup, any mistakes and an A+ to B- grade.',
      action: 'Review a trade',
      done: trades.some((x) => (x.tags && x.tags.length) || x.grade),
    },
    {
      id: 'reflection',
      title: 'Finish an End-of-day reflection',
      text: 'Score your day, check off your rules and set one thing to work on.',
      action: 'Open reflection',
      done: days.some((n) => n.score || n.review || (n.rulesFollowed && n.rulesFollowed.length) || (n.moodAfter && n.moodAfter.length)),
    },
    {
      id: 'playbook',
      title: 'Write your first play',
      text: 'Turn a setup you trade into a play with clear rules.',
      action: 'Open Playbook',
      done: plays.length > 0,
    },
  ];
}

function GettingStarted({ user, accounts, trades, notes, plays, onAction }) {
  const { t } = useI18n();
  const [hidden, setHidden] = useState(() => readHidden(user));
  const steps = onboardingSteps({ accounts, trades, notes, plays });
  const done = steps.filter((s) => s.done).length;
  if (hidden || done === steps.length) return null;
  const next = steps.find((s) => !s.done);

  function hide() {
    setHidden(true);
    try {
      localStorage.setItem(hiddenKey(user), '1');
    } catch {
      // Not remembered in private mode; it hides for this visit.
    }
  }

  return (
    <section className="onboarding" aria-label={t('Getting started')}>
      <div className="onboarding-head">
        <div>
          <span className="unlock-label">{t('Getting started')}</span>
          <h2>{done === 0 ? t('Welcome to Market Matrix, {name}!', { name: user.name.split(' ')[0] }) : t('Keep going, you’re setting up well')}</h2>
          <p className="onboarding-sub">{t('{done} of {total} done. These steps take about 10 minutes and set up everything Market Matrix needs.', { done, total: steps.length })}</p>
        </div>
        <button type="button" className="link-button" onClick={hide}>{t('Hide this')}</button>
      </div>
      <div className="status-bar onboarding-bar" role="img" aria-label={t('{done} of {total} steps done', { done, total: steps.length })}>
        <span className="status-bar-fill target" style={{ width: `${(done / steps.length) * 100}%` }} />
      </div>
      <ol className="onboarding-steps">
        {steps.map((s, i) => (
          <li key={s.id} className={`onboarding-step${s.done ? ' done' : ''}${next && next.id === s.id ? ' next' : ''}`}>
            <span className="onboarding-check" aria-hidden="true">{s.done ? '✓' : i + 1}</span>
            <span className="onboarding-text">
              <strong>{t(s.title)}</strong>
              <span>{t(s.text)}</span>
            </span>
            {!s.done && (
              <button type="button" className={next && next.id === s.id ? 'small' : 'secondary small'} onClick={() => onAction(s.id)}>
                {t(s.action)}
              </button>
            )}
          </li>
        ))}
      </ol>
    </section>
  );
}

export default GettingStarted;
